const TELEPORTE_API = 'https://teleporte.odd-scene-3e1d.workers.dev/api';

async function safeJson(response) {
  const text = await response.text();
  if (!text || text.trim().startsWith('<')) return { ok: false, error: 'non_json_response' };
  try { return { ok: true, data: JSON.parse(text) }; }
  catch (error) { return { ok: false, error: error.message || 'invalid_json' }; }
}

async function getJson(path) {
  try {
    const relative = String(path || '').replace(/^\/+/, '');
    const sameOrigin = `/api/${relative}${relative.includes('?') ? '&' : '?'}_t=${Date.now()}`;
    const response = await fetch(sameOrigin, {
      cache: 'no-store', credentials: 'include', headers: { accept: 'application/json' },
    });
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.error, error: parsed.error };
    return response.ok
      ? { success: true, ...parsed.data }
      : { success: false, code: parsed.data?.error || `http_${response.status}`, error: parsed.data?.error || `HTTP ${response.status}`, ...parsed.data };
  } catch (error) {
    return { success: false, code: 'network', error: error.message || String(error) };
  }
}

export function verifyBackroomLedgerWindow(messages = [], cursor = {}) {
  const rows = Array.isArray(messages) ? [...messages] : [];
  rows.sort((a,b)=>Number(a?.thread_seq||0)-Number(b?.thread_seq||0));
  let valid = true;
  const errors = [];
  for (let i=1;i<rows.length;i+=1) {
    const prev=rows[i-1];
    const current=rows[i];
    if (Number(current.thread_seq) !== Number(prev.thread_seq)+1) {
      valid=false; errors.push(`SEQ_GAP:${prev.thread_seq}->${current.thread_seq}`);
    }
    if (current.previous_envelope_hash && prev.envelope_hash && current.previous_envelope_hash !== prev.envelope_hash) {
      valid=false; errors.push(`HASH_LINK_MISMATCH:${current.thread_seq}`);
    }
  }
  const last=rows.at(-1) || null;
  const coversTip = Number(cursor?.through_seq||0) === Number(cursor?.latest_seq||0);
  if (coversTip && cursor?.ledger_tip_hash && last?.envelope_hash && cursor.ledger_tip_hash !== last.envelope_hash) {
    valid=false; errors.push('LEDGER_TIP_MISMATCH');
  }
  return {
    status: valid ? 'VERIFIED' : 'INVALID',
    valid,
    errors,
    from_seq:Number(rows[0]?.thread_seq||0),
    through_seq:Number(last?.thread_seq||0),
    latest_seq:Number(cursor?.latest_seq||0),
    observed_ledger_tip:cursor?.ledger_tip_hash||null,
    window_tip:last?.envelope_hash||null,
  };
}

async function cockpitWrite(payload) {
  try {
    const response = await fetch('/api/backroom-v2-write', {
      method: 'POST', credentials: 'include', cache: 'no-store',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(payload || {}),
    });
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success: false, code: parsed.error, error: parsed.error };
    return response.ok
      ? { success: true, ...parsed.data }
      : { success: false, code: parsed.data?.error || `http_${response.status}`, error: parsed.data?.error || `HTTP ${response.status}`, ...parsed.data };
  } catch (error) {
    return { success: false, code: 'network', error: error.message || String(error) };
  }
}

export async function fetchBackroomV22Messages(channel, { afterSeq = 0, limit = 100 } = {}) {
  const safeLimit = Math.max(1, Math.min(200, Number(limit) || 100));
  const safeAfter = Math.max(0, Number(afterSeq) || 0);
  const result = await getJson(`/backroom-v2/${encodeURIComponent(channel)}/messages?after_seq=${safeAfter}&limit=${safeLimit}`);
  if (result.success) result.ledger_integrity = verifyBackroomLedgerWindow(result.messages, result.cursor);
  return result;
}

export async function fetchBackroomV22Summary(channel) {
  return getJson(`/backroom-v2/${encodeURIComponent(channel)}/summary`);
}

export async function fetchBackroomV22Presence(channel) {
  return getJson(`/backroom-v2/${encodeURIComponent(channel)}/presence`);
}

export function createBackroomOperationId() {
  return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export async function postBackroomV22FromCockpit(channel, message, { idempotencyKey = null } = {}) {
  const key = String(idempotencyKey || '').trim();
  if (!key) return { success:false, code:'idempotency_required', error:'Retry-stable Backroom operation id required.' };
  return cockpitWrite({ operation:'message', channel, idempotency_key:key, message:message || {} });
}

export async function postBackroomV22PresenceFromCockpit(channel, presence = {}) {
  return cockpitWrite({ operation: 'presence', channel, ...presence });
}

/** Direct bearer path retained for native/service clients. Callers own retry-stable idempotency identity. */
export async function postBackroomV22Message(channel, message, { bearer = null, idempotencyKey = null, tonyApprovalToken = null } = {}) {
  if (!bearer) return { success:false, code:'auth_required', error:'Authenticated Teleporte agent bearer required for direct Backroom writes.' };
  const key = String(idempotencyKey || '').trim();
  if (!key) return { success:false, code:'idempotency_required', error:'Retry-stable Backroom operation id required.' };
  const headers = { accept:'application/json', 'content-type':'application/json', authorization:`Bearer ${bearer}`, 'Idempotency-Key':key };
  if (tonyApprovalToken) {
    headers['x-teleporte-tony-approved'] = 'true';
    headers['x-teleporte-tony-approval-token'] = tonyApprovalToken;
  }
  try {
    const response = await fetch(`${TELEPORTE_API}/backroom-v2/${encodeURIComponent(channel)}/envelopes`, {
      method:'POST', headers, body:JSON.stringify(message || {}), cache:'no-store',
    });
    const parsed = await safeJson(response);
    if (!parsed.ok) return { success:false, code:parsed.error, error:parsed.error };
    return response.ok
      ? { success:true, ...parsed.data }
      : { success:false, code:parsed.data?.error || `http_${response.status}`, error:parsed.data?.error || `HTTP ${response.status}`, ...parsed.data };
  } catch (error) {
    return { success:false, code:'network', error:error.message || String(error) };
  }
}

export function normalizeV22Message(message = {}) {
  const typed = message.protocol_id === 'teleporte.backroom.coordination' || String(message.schema_version || '').startsWith('2.');
  const kind = message.kind == null ? null : String(message.kind).trim().toUpperCase();
  return {
    id: message.message_id || message.id,
    message_id: message.message_id || message.id,
    thread_seq: Number(message.thread_seq || 0),
    principal: typed ? (message.principal || null) : (message.principal || message.source_chat || 'legacy-unknown'),
    source_chat: typed ? (message.principal || null) : (message.principal || message.source_chat || 'legacy-unknown'),
    kind,
    task_id: message.task_id || null,
    correlation_id: message.correlation_id || null,
    reply_to: message.reply_to || null,
    root_message_id: message.root_message_id || null,
    causal_depth: Number(message.causal_depth || 0),
    requires_ack: Boolean(message.requires_ack),
    ack_policy: message.ack_policy || null,
    observed_canonical_revision: message.observed_canonical_revision ?? (typed ? null : message.canonical_revision_ref ?? null),
    accepted_canonical_revision: message.accepted_canonical_revision ?? null,
    payload_hash: message.payload_hash || null,
    previous_envelope_hash: message.previous_envelope_hash || null,
    envelope_hash: message.envelope_hash || null,
    content: String(message.content || ''),
    evidence: Array.isArray(message.evidence) ? message.evidence : [],
    metadata: message.metadata || {},
    created_at: message.created_at || null,
    protocol_id: message.protocol_id || null,
    schema_version: message.schema_version || null,
    unsupported: typed && (!message.principal || !kind),
  };
}